import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { message, Modal } from "antd";
import AttachmentTable from "components/table/AttachmentTable";
import BankTable from "components/table/BankTable";
import CareerTable from "components/table/CareerTable";
import CompanyRegulationTable from "components/table/CompanyRegulationTable";
import CourseCategoryTable from "components/table/CourseCategoryTable";
import EmployeeMasterDataTable from "components/table/EmployeeMasterDataTable";
import LeavingTable from "components/table/LeavingTable";
import ReasonTable from "components/table/ReasonTable";
import SkillSetTable from "components/table/SkillSetTable";
import SkillTypeTable from "components/table/SkillTypeTable";

import { generateRandomString } from "utils/helper";
import schema, {
  initialValues as INITIAL_VALUES,
  MASTER_DATA_TYPE,
  MstReasonSchema,
} from "../schemas/masterDataSchema";
import * as services from "../services/masterDataApi";
import { setIsLoading, setMasterDataList } from "../slices/masterDataSlice";
import useOptions from "./useOptions";
import { getEmployeeTypeOptions } from "../services/optionsApi";
import { setEmployeeTypeOptions } from "../slices/optionsSlice";

const MODAL_TYPE = {
  CREATE: "create",
  EDIT: "edit",
  CHANGE_STATUS: "change status"
};

function toInitialValues(initialValues = {}) {
  const { reasonForSSO = {} } = initialValues;
  const modifiedValues = {
    ...initialValues,
    containPayrollData: initialValues.containPayrollData === "Y",
    docSelfUpload: initialValues.docSelfUpload === "Y",
    requireContractExpiryDate: initialValues.requireContractExpiryDate === "Y",
    reasonForSSOId: initialValues?.reasonForSSO?.id || null,
  };
  return modifiedValues;
}

function toParams(params) {
  const { pagination = {}, sortBy = [] } = params;
  const modifiedParams = {
    ...params,
    docSelfUpload: params.docSelfUpload ? "Y" : "N",
    containPayrollData: params.containPayrollData ? "Y" : "N",
    requireContractExpiryDate: params.requireContractExpiryDate ? "Y" : "N",
    page: pagination.page || 0,
    size: pagination.pageSize || 10,
    sortBy: sortBy
      .filter((s) => s.order !== undefined)
      .map((s) => ({
        direction: s.order === "descend" ? "desc" : "asc",
        property: s.field,
      })),
  };
  return modifiedParams;
}

function useMasterData() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const isLoading = useSelector((state) => state.masterData.isLoading);
  const masterDataList = useSelector(
    (state) => state.masterData.masterDataList || []
  );
  const [currentMasterData, setCurrentMasterData] = useState({});

  const {
    getEmployeeTypeOptions,
  } = useOptions();

  const { roles } = useSelector((state) => state.company);
  const [selectedMenuRole, setMenuRole] = useState('');
  const [modalType, setModalType] = useState(MODAL_TYPE.CREATE);
  const [pagination, setPagination] = useState({ page: 1, size: 10 });
  const [sorter, setSorter] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const { reasonForSSOOptions = [], getReasonForSSOOptions } = useOptions();
  const [useFilter, setUseFilter] = useState(false);

  const labelList = useMemo(() => {
    return {
      [MASTER_DATA_TYPE.BANK]: [t('master_data.label.bank-name-th'), t('master_data.label.bank-name-en')],
      [MASTER_DATA_TYPE.CAREER]: [t('master_data.label.career-name-th'), t('master_data.label.career-name-en')],
      [MASTER_DATA_TYPE.REASON_FOR_LEAVING]: [t('master_data.label.reason-th'), t('reason-en')],
      [MASTER_DATA_TYPE.ATTACHMENT_TYPE]: [
        t('master_data.label.attach-type-th'),
        t('master_data.label.attach-type-en'),
        {
          label: t('master_data.label.this-document-contains-payroll-data'),
          name: "containPayrollData",
          type: "switch",
        },
        {
          label: t('master_data.label.employee-document-self-upload'),
          name: "docSelfUpload",
          type: "switch",
        },
      ],
      [MASTER_DATA_TYPE.EMPLOYEE_TYPE]: [
        t('master_data.label.employee-type-th'),
        t('master_data.label.employee-type-en'),
        {
          label: t('master_data.label.require-contract-expiry-date'),
          name: "requireContractExpiryDate",
          type: "switch",
        },
      ],
      [MASTER_DATA_TYPE.COMPANY_REGULATION]: [
        t('master_data.label.regulation-th'),
        t('master_data.label.regulation-en'),
      ],
      [MASTER_DATA_TYPE.COURSE_CATEGORY]: [
        t('master_data.label.course-category-th'),
        t('master_data.label.course-category-en'),
      ],
      [MASTER_DATA_TYPE.SKILL_TYPE]: [
        t('master_data.label.skills-type-th'),
        t('master_data.label.skills-type-en'),
      ],
      [MASTER_DATA_TYPE.SKILL_SET]: [t('master_data.label.skill-set-th'), t('master_data.label.skill-set-en')],
      [MASTER_DATA_TYPE.REASON_FOR_SEPARATION]: [
        t('master_data.label.reason-for-separation-th'),
        t('master_data.label.reason-for-separation-en'),
        {
          label: t('master_data.label.apply-to-reason-for-sso-resignation'),
          name: "reasonForSSOId",
          type: "select",
          options: reasonForSSOOptions,
        },
      ],
    };
  }, [reasonForSSOOptions]);

  const getDefaultMenu = () => {
    const menuMapping = {
      'ACCESS_CONFIGURATION|COMMON_MASTER|BANK': MASTER_DATA_TYPE.BANK,
      'ACCESS_CONFIGURATION|COMMON_MASTER|CAREER': MASTER_DATA_TYPE.CAREER,
      'ACCESS_CONFIGURATION|COMMON_MASTER|REASON_OF_LEAVING': MASTER_DATA_TYPE.REASON_FOR_LEAVING,
      'ACCESS_CONFIGURATION|COMMON_MASTER|ATTACHMENT_TYPE': MASTER_DATA_TYPE.ATTACHMENT_TYPE,
      'ACCESS_CONFIGURATION|COMMON_MASTER|EMPLOYEE_TYPE': MASTER_DATA_TYPE.EMPLOYEE_TYPE,
      'ACCESS_CONFIGURATION|COMMON_MASTER|COMPANY_REGULATION': MASTER_DATA_TYPE.COMPANY_REGULATION,
      'ACCESS_CONFIGURATION|COMMON_MASTER|COURCE_CATEGORY': MASTER_DATA_TYPE.COURSE_CATEGORY,
      'ACCESS_CONFIGURATION|COMMON_MASTER|SKILL_TYPE': MASTER_DATA_TYPE.SKILL_TYPE,
      'ACCESS_CONFIGURATION|COMMON_MASTER|SKILL_SET': MASTER_DATA_TYPE.SKILL_SET,
      'ACCESS_CONFIGURATION|COMMON_MASTER|REASON_OF_SEPARATION': MASTER_DATA_TYPE.REASON_FOR_SEPARATION
    };

    let mappingList = Object.keys(menuMapping)
    let key = null;
    mappingList.forEach(o => {
      const firstAvailableRole = roles.some(p => p == o);
      console.log("firstAvailableRole", menuMapping[o]);
      if (firstAvailableRole && !key) {
        key = menuMapping[o]
      }
    })

    return key;
  };
  const [selectedMenu, setMenu] = useState(getDefaultMenu());

  const initialValues = useMemo(() => {
    return toInitialValues(currentMasterData);
  }, [currentMasterData]);


  const getMasterDataList = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getCommonTypeCompany({
          ...params,
          requestId: generateRandomString(),
          companyId: currentCompany,
          groupTypeCode: selectedMenu,
        });
        dispatch(
          setMasterDataList(response.data?.content || [])
        );
        setPagination({
          page: params.page + 1,
          size: params.size,
          range: [
            params.page * params.size + 1,
            Math.min((params.page + 1) * params.size, response.data?.totalItems),
          ],
          total: response.data?.totalItems,
        });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, selectedMenu, dispatch]
  );
  const onSearch = useCallback(
    async (values) => {
      values.page = 0;
      values.size = 50;
      getMasterDataList(values)
    },
    [currentCompany, selectedMenu, dispatch]
  );

  const onEdit = (record) => {
    setModalType(MODAL_TYPE.EDIT);
    setCurrentMasterData(record);
    setModalOpen(true);
  };

  const onCreate = () => {
    setModalType(MODAL_TYPE.CREATE);
    setCurrentMasterData({});
    setModalOpen(true);
  };

  const onCancel = () => {
    setCurrentMasterData({});
    setModalOpen(false);
  };



  const onStatus = useCallback(
    async (val, record) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          id: record.id,
          requestId: generateRandomString(),
          companyId: currentCompany,
          statusFlag: val ? "ACTIVE" : "INACTIVE",
        };
        Modal.confirm({
          title: t("dialog.confirmation.header"),
          content: (i18n.language === "en" ?
            <p>
              Are you sure confirm to change status the data ?
            </p>
            :
            <p>
              คุณยืนยันการเปลี่ยนสถานะข้อมูลหลักใช่หรือไม่ ?
            </p>
          ),
          async onOk() {
            await services.manageStatusCommonTypeCompany(values);
            const modifiedParams = toParams({
              page: pagination.page - 1,
              size: pagination.size,
              sortBy: sorter,
            });
            message.success(t("common.success"))
            getMasterDataList(modifiedParams);
          },
          okText: t("common.confirm"),
          cancelText: t("common.cancel"),
        });


      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, sorter, dispatch, getMasterDataList]
  );

  const onChange = useCallback(
    (tablePagination, _, tableSorter) => {
      setPagination(tablePagination);
      const modifiedSorter = Array.isArray(tableSorter)
        ? [...tableSorter]
        : [tableSorter];
      setSorter(modifiedSorter);
      const modifiedParams = toParams({
        pagination: {
          page: tablePagination.current - 1,
          pageSize: tablePagination.pageSize,
        },
        sortBy: modifiedSorter,
      });
      // TODO: If sorting or filtering has changed, Change page to 0
      getMasterDataList(modifiedParams);

    },
    [getMasterDataList]
  );

  const createCommonTypeCompany = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        const result = await services.createCommonTypeCompany(
          toParams({
            ...values,
            groupTypeCode: selectedMenu,
            companyId: currentCompany,
          })
        );
        setCurrentMasterData({});
        setModalOpen(false);
        dispatch(setEmployeeTypeOptions([]))
        getEmployeeTypeOptions({
          companyId: currentCompany,
          groupType: "EMPLOYEE_TYPE",
        });
        getMasterDataList({
          page: 0,
          size: 10,
        });
        onCancel()
        message.success(t("common.success"))
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [selectedMenu, currentCompany, dispatch, getMasterDataList]
  );

  const updateCommonTypeCompany = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        await services.updateCommonTypeCompany(
          toParams({
            ...values,
            groupTypeCode: selectedMenu,
            companyId: currentCompany,
          })
        );
        message.success(t("common.success"))
        setCurrentMasterData({});
        setModalOpen(false);
        dispatch(setEmployeeTypeOptions([]))
        getEmployeeTypeOptions({
          companyId: currentCompany,
          groupType: "EMPLOYEE_TYPE",
        });
        getMasterDataList({
          page: 0,
          size: 10,
        });
        onCancel()
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [selectedMenu, currentCompany, dispatch, getMasterDataList]
  );

  const onSubmit = useCallback(
    (values) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en" ?
          <p>
            Are you sure confirm to{" "}
            {modalType === MODAL_TYPE.CREATE ? "create" : "edit"} the data ?
          </p>
          :
          <p>
            คุณยืนยันการ
            {modalType === MODAL_TYPE.CREATE ? "สร้าง" : "แก้ไข"}ข้อมูลหลักใช่หรือไม่ ?
          </p>
        ),
        onOk() {
          if (values.id) {
            updateCommonTypeCompany(values);
          } else {
            createCommonTypeCompany(values);
          }
        },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    },
    [modalType, updateCommonTypeCompany, createCommonTypeCompany]
  );

  const handleCancel = useCallback(() => {
    // Modal.confirm({
    //   title: t("dialog.confirmation.header"),
    //   content:
    //     i18n.language === "en"
    //       ? "Are you sure you want to cancel the changes?"
    //       : <p>
    //         คุณต้องการยกเลิกการ
    //         {modalType === MODAL_TYPE.CREATE ? "สร้าง" : "แก้ไข"}ใช่หรือไม่ ?
    //       </p>,
    //   onOk() {
    onCancel();
    //   },
    //   okText: t("common.confirm"),
    //   cancelText: t("common.cancel"),
    // });
  }, [onCancel, t, i18n.language]);

  const handleMenuChange = useCallback(
    (e) => {
      setMenu(e);
      setMenuRole(e.rules)

      setUseFilter(false);
    },
    [setMenu, setMenuRole]
  );

  const handleDelete = useCallback(
    async (record) => {
      try {
        dispatch(setIsLoading(true));
        const values = {
          id: record.id,
          requestId: generateRandomString(),
          companyId: currentCompany,
          statusFlag: "DELETE",
        };
        await services.manageStatusCommonTypeCompany(values);
        message.success(t("common.success"))
        const modifiedParams = toParams({
          page: pagination.page - 1,
          pageSize: pagination.size,
          sortBy: sorter,
        });
        getMasterDataList(modifiedParams);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, pagination, sorter, dispatch, getMasterDataList]
  );

  const onDelete = useCallback(
    (record) => {
      Modal.confirm({
        title: t('confirmation-0'),
        content: (
          <>
            <p>{t('are-you-sure-confirm-to-delete-the-data')}</p>
          </>
        ),
        onOk() {
          handleDelete(record);
        },
        okText: t('confirm-1'),
        cancelText: t('masterData.cancel'),
      });
    },
    [handleDelete]
  );

  const modalProps = useMemo(() => {
    return {
      title:
        modalType === MODAL_TYPE.CREATE ? t("common.create") : t("common.edit"),
      labelList: labelList[selectedMenu] || [],
      submit: modalType === MODAL_TYPE.CREATE ? "CREATE" : "SAVE",
      schema:
        selectedMenu === MASTER_DATA_TYPE.REASON_FOR_SEPARATION
          ? MstReasonSchema
          : schema,
      initialValues:
        selectedMenu === MASTER_DATA_TYPE.REASON_FOR_SEPARATION
          ? { ...INITIAL_VALUES, reasonForSSOId: null, ...initialValues }
          : { ...INITIAL_VALUES, ...initialValues },
    };
  }, [labelList, initialValues, modalType, selectedMenu, t]);

  const getPage = () => {
    switch (selectedMenu) {
      case MASTER_DATA_TYPE.BANK:
        return (
          <BankTable
            isLoading={isLoading}
            pagination={pagination}
            dataSource={masterDataList}
            onEdit={onEdit}
            onDelete={onDelete}
            onStatus={onStatus}
            onChange={onChange}
          />
        );
      case MASTER_DATA_TYPE.CAREER:
        return (
          <CareerTable
            isLoading={isLoading}
            pagination={pagination}
            dataSource={masterDataList}
            onEdit={onEdit}
            onDelete={onDelete}
            onStatus={onStatus}
            onChange={onChange}
          />
        );
      case MASTER_DATA_TYPE.REASON_FOR_LEAVING:
        return (
          <LeavingTable
            isLoading={isLoading}
            pagination={pagination}
            dataSource={masterDataList}
            onEdit={onEdit}
            onDelete={onDelete}
            onStatus={onStatus}
            onChange={onChange}
          />
        );
      case MASTER_DATA_TYPE.ATTACHMENT_TYPE:
        return (
          <AttachmentTable
            isLoading={isLoading}
            pagination={pagination}
            dataSource={masterDataList}
            onEdit={onEdit}
            onDelete={onDelete}
            onStatus={onStatus}
            onChange={onChange}
          />
        );
      case MASTER_DATA_TYPE.EMPLOYEE_TYPE:
        return (
          <EmployeeMasterDataTable
            isLoading={isLoading}
            pagination={pagination}
            dataSource={masterDataList}
            onEdit={onEdit}
            onDelete={onDelete}
            onStatus={onStatus}
            onChange={onChange}
          />
        );
      case MASTER_DATA_TYPE.COMPANY_REGULATION:
        return (
          <CompanyRegulationTable
            isLoading={isLoading}
            pagination={pagination}
            dataSource={masterDataList}
            onEdit={onEdit}
            onDelete={onDelete}
            onStatus={onStatus}
            onChange={onChange}
          />
        );
      case MASTER_DATA_TYPE.COURSE_CATEGORY:
        return (
          <CourseCategoryTable
            isLoading={isLoading}
            pagination={pagination}
            dataSource={masterDataList}
            onEdit={onEdit}
            onDelete={onDelete}
            onStatus={onStatus}
            onChange={onChange}
          />
        );
      case MASTER_DATA_TYPE.SKILL_TYPE:
        return (
          <SkillTypeTable
            isLoading={isLoading}
            pagination={pagination}
            dataSource={masterDataList}
            onEdit={onEdit}
            onDelete={onDelete}
            onStatus={onStatus}
            onChange={onChange}
          />
        );
      case MASTER_DATA_TYPE.SKILL_SET:
        return (
          <SkillSetTable
            isLoading={isLoading}
            pagination={pagination}
            dataSource={masterDataList}
            onEdit={onEdit}
            onDelete={onDelete}
            onStatus={onStatus}
            onChange={onChange}
          />
        );
      case MASTER_DATA_TYPE.REASON_FOR_SEPARATION:
        return (
          <ReasonTable
            isLoading={isLoading}
            pagination={pagination}
            dataSource={masterDataList}
            onEdit={onEdit}
            onDelete={onDelete}
            onStatus={onStatus}
            onChange={onChange}
          />
        );
      default:
        return <div />;
    }
  }

  useEffect(() => {
    if (typeof currentCompany !== "object" && currentCompany !== null) {
      const hasPermission = roles.some(role => {
        let value = selectedMenu
        if (selectedMenu === MASTER_DATA_TYPE.REASON_FOR_LEAVING) value = MASTER_DATA_TYPE.REASON_OF_LEAVING
        if (selectedMenu === MASTER_DATA_TYPE.COURSE_CATEGORY) value = MASTER_DATA_TYPE.COURCE_CATEGORY
        if (selectedMenu === MASTER_DATA_TYPE.REASON_FOR_SEPARATION) value = MASTER_DATA_TYPE.REASON_OF_SEPARATION
        return role.includes("ACCESS_CONFIGURATION|COMMON_MASTER|" + value)
      }
      );
      if (!hasPermission) {
        setMenu(getDefaultMenu());
      }

      getMasterDataList({
        page: 0,
        size: 10,
        groupTypeCode: selectedMenu,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany, selectedMenu, roles]);

  useEffect(() => {
    getReasonForSSOOptions();
  }, [getReasonForSSOOptions]);

  return {
    isLoading,
    selectedMenu,
    isModalOpen,
    modalProps,
    onDelete,
    onEdit,
    onCreate,
    handleMenuChange,
    onCancel: handleCancel,
    onSubmit,
    onSearch,
    getPage,
    useFilter,
    setUseFilter
  };
}

export default useMasterData;
